import { useMemo, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { PwcLoader } from '@pwc/pwc-deals-ui';

import {
  AutoCreate as BaseAutoCreate,
  AutoCreateComponentProps,
} from '@pwc/auto-save-ui';

import {
  showGlobalError,
  hideGlobalError,
} from 'common/redux/errors/errorsSlice';

import SupportTicketLink from '../SupportTicketLink';

import styles from './AutoCreate.module.scss';

export enum ContentType {
  KeyFocusArea = 'Key Focus Area',
  FreeFormAnalysis = 'Free Form Analysis',
  StructuredAnalysis = 'Structured Analysis',
  POTopic = 'Topic',
}

type MakeAutoCreateProps = {
  children?: React.ReactNode;
  contentType: ContentType;
  errorClasses?: string;
  loaderClasses?: string;
};

const error = {
  id: '#auto-create-error',
  eventType: 'newPage',
  error: {
    code: 'AUTO_CREATE',
  },
  metadata: {
    pageMetadata: {
      inlineIcon: true,
      icon: 'alert',
      buttonText: 'Retry',
      buttonTitle: 'Retry',
      iconTitle: 'Alert icon',
      secondarySubTitle: (
        <>
          Please try again or{' '}
          <SupportTicketLink className={styles.supportTicketLink}>
            create a support ticket
          </SupportTicketLink>{' '}
          if the issue persists.
        </>
      ),
      showSecondaryButton: false,
    },
  },
};

const makeAutoCreate: (
  props: MakeAutoCreateProps
) => React.FC<AutoCreateComponentProps<unknown>> = ({
  children,
  contentType,
  errorClasses,
  loaderClasses,
}) => {
  return (props) => {
    const { isLoading, isError, mutate } = props;

    const dispatch = useDispatch();

    useEffect(() => {
      if (isError) {
        dispatch(
          showGlobalError({
            ...error,
            metadata: {
              pageMetadata: {
                ...error.metadata.pageMetadata,
                header: `Unable to create ${contentType}`,
                subTitle: `Due to a system error, we couldn't create this ${contentType.toLowerCase()}.`,
              },
            },
            confirm: mutate,
          })
        );
      }

      return () => {
        dispatch(hideGlobalError());
      };
    }, [mutate, isError, dispatch]);

    const renderChildren =
      children instanceof Function
        ? (children(props) as React.ReactNode)
        : children;

    return (
      <>
        {isLoading ? (
          <div className={loaderClasses}>
            <PwcLoader loaderType="large" loading />
          </div>
        ) : (
          renderChildren
        )}
        {isError && (
          <div className={errorClasses}>
            <div id="auto-create-error" />
          </div>
        )}
      </>
    );
  };
};

type AutoCreateProps = React.ComponentProps<typeof BaseAutoCreate> &
  MakeAutoCreateProps;

export function AutoCreate({
  children,
  createApiEndpointUrl,
  makeEditUrl,
  httpClient,
  contentType,
  loaderClasses,
  errorClasses,
}: AutoCreateProps) {
  const component = useMemo(
    () =>
      makeAutoCreate({ children, contentType, loaderClasses, errorClasses }),
    [children, contentType, loaderClasses, errorClasses]
  );

  return (
    <BaseAutoCreate
      createApiEndpointUrl={createApiEndpointUrl}
      makeEditUrl={makeEditUrl}
      httpClient={httpClient}
    >
      {component}
    </BaseAutoCreate>
  );
}

export default AutoCreate;
